import { useState, useCallback, useMemo } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import {
  getTechnicianAssignments,
  getTechnicianAssignmentsByTechnician,
  createTechnicianAssignment,
  updateTechnicianAssignment,
  deleteTechnicianAssignment,
  getTechnicianWorkloadSummary,
  getAvailableTechniciansForCampaign,
  getAllActiveTechnicians,
  checkExistingAssignment,
  TechnicianAssignment,
  CreateAssignmentData,
  UpdateAssignmentData
} from '@/lib/supabase/technicianAssignments';
import {
  createDataLoadingError,
  createUserActionError,
  ErrorMetadata
} from '@/lib/utils/errorTranslator';
import {
  syncAssignmentsToRoute,
  formatRouteDate,
  deleteRouteByTechnicianDate,
  getRouteByTechnicianDate
} from '@/lib/supabase/technicianRoutes';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { routeKeys } from '@/features/routing/hooks/useTechnicianRoutes';
import { logUserActivity } from '@/lib/activity/userActivity';

// Hierarchical React Query keys for technician assignment data. Shared across
// every component that mounts this hook, so concurrent mounts dedupe to a single
// network request per key instead of each running its own full-table load.
export const assignmentKeys = {
  all: ['technician-assignments'] as const,
  list: () => [...assignmentKeys.all, 'list'] as const,
  workload: () => [...assignmentKeys.all, 'workload'] as const,
  technicians: () => [...assignmentKeys.all, 'active-technicians'] as const
};

const useTechnicianAssignments = () => {
  const queryClient = useQueryClient();
  // Action-level error state (create/update/delete). Load errors are derived
  // from the React Query error below and merged into the returned value.
  const [error, setError] = useState<string | null>(null);
  const [errorMetadata, setErrorMetadata] = useState<ErrorMetadata | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // React Query is the single source of truth for assignment data. Crucially,
  // when a refetch fails (e.g. statement timeout on a large tenant) React Query
  // KEEPS the last successful data and exposes the failure via `.error` — it
  // never replaces a good list with []. That alone eliminates the bug where a
  // transient load failure zeroed every technician's count and flooded the
  // available-recall list with already-processed work.
  const assignmentsQuery = useQuery({
    queryKey: assignmentKeys.list(),
    queryFn: getTechnicianAssignments,
    staleTime: 30 * 1000
  });

  const workloadQuery = useQuery({
    queryKey: assignmentKeys.workload(),
    queryFn: getTechnicianWorkloadSummary,
    staleTime: 60 * 1000
  });

  const techniciansQuery = useQuery({
    queryKey: assignmentKeys.technicians(),
    queryFn: async () => {
      const result = await getAllActiveTechnicians();
      if (result.error) throw result.error;
      return result.data ?? [];
    },
    staleTime: 60 * 1000
  });

  const assignments = useMemo(() => assignmentsQuery.data ?? [], [assignmentsQuery.data]);
  const workloadSummary = useMemo(() => workloadQuery.data ?? [], [workloadQuery.data]);
  const activeTechnicians = useMemo(() => techniciansQuery.data ?? [], [techniciansQuery.data]);
  const loading = assignmentsQuery.isLoading;

  // Derive a user-facing load-error message from the query error so a failed
  // load still surfaces (without wiping the last good data).
  const loadErrorMetadata = useMemo<ErrorMetadata | null>(() => {
    if (!assignmentsQuery.error) return null;
    return createDataLoadingError(assignmentsQuery.error, 'loading technician assignments data');
  }, [assignmentsQuery.error]);

  // Optimistic cache helper: patch the assignment list synchronously so the UI
  // (counts + lists) updates instantly, before any network round-trip.
  const patchAssignments = useCallback(
    (updater: (prev: TechnicianAssignment[]) => TechnicianAssignment[]) => {
      queryClient.setQueryData<TechnicianAssignment[]>(assignmentKeys.list(), prev =>
        updater(prev ?? [])
      );
    },
    [queryClient]
  );

  /**
   * Sync assignments to the technician_routes table
   * This ensures route preview and other pages show current data
   * @param knownAssignmentIds - Optional IDs that were just inserted/updated by
   *   the caller. Same race-mitigation pattern as syncAssignmentsToRouteAsync in
   *   useAutoSaveAssignment: when provided, skip the 300ms wait and patch any
   *   race-induced misses via PK fetch. Eliminates the silent-orphan failure
   *   mode where the list-query missed fresh inserts and the function bailed.
   */
  const syncToRouteTable = useCallback(
    async (technicianId: string, expectedVisitDate: string, knownAssignmentIds?: string[]) => {
      try {
        // Race-condition workaround: only needed when no known IDs to patch with.
        if (!knownAssignmentIds || knownAssignmentIds.length === 0) {
          // Small delay to ensure the database transaction has committed
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        // Fetch all assignments for this technician on this date
        const allAssignments = (await getTechnicianAssignments()) ?? [];

        // Patch in any race-induced misses via direct PK fetch (PK lookups are
        // not subject to the visibility lag that affects RLS-filtered list queries).
        if (knownAssignmentIds && knownAssignmentIds.length > 0) {
          const seenInRequery = new Set(allAssignments.map(a => a.id));
          const missingIds = knownAssignmentIds.filter(id => !seenInRequery.has(id));
          if (missingIds.length > 0) {
            // eslint-disable-next-line no-console
            console.log(
              `[syncToRouteTable] Re-query missed ${missingIds.length} known IDs, fetching by PK`
            );
            try {
              const prodClient = getProdSupabaseClient();
              const { data: patchedRows, error: patchError } = await prodClient
                .from('technician_assignments')
                .select(
                  '*, technicians!inner(id, full_name, phone_number, email, skill_level, is_active)'
                )
                .in('id', missingIds);
              if (patchError) {
                // eslint-disable-next-line no-console
                console.warn('[syncToRouteTable] PK-fetch patch failed:', patchError);
              } else if (patchedRows && Array.isArray(patchedRows)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const transformed = (patchedRows as any[]).map((a: any) => ({
                  ...a,
                  technician_name: a.technicians?.full_name,
                  technician_phone: a.technicians?.phone_number,
                  technician_email: a.technicians?.email,
                  technician_skill_level: a.technicians?.skill_level,
                  technician_active: a.technicians?.is_active
                }));
                allAssignments.push(...transformed);
              }
            } catch (patchCatchErr) {
              // eslint-disable-next-line no-console
              console.warn('[syncToRouteTable] PK-fetch patch threw:', patchCatchErr);
            }
          }
        }

        if (allAssignments.length === 0) {
          // No assignments at all - delete any existing route for this date
          await deleteRouteByTechnicianDate(technicianId, expectedVisitDate);
          // Invalidate route queries so other pages get fresh data
          queryClient.invalidateQueries({ queryKey: routeKeys.all });
          return;
        }

        // Filter to assignments for this technician on this date
        // Exclude completed and cancelled assignments - they're already on a completed route
        const routeDate = formatRouteDate(expectedVisitDate);
        const technicianAssignments = allAssignments.filter(a => {
          if (a.technician_id !== technicianId) return false;
          if (!a.expected_visit_date) return false;
          // Don't include assignments that are already completed or cancelled
          // These are already accounted for in a completed route
          if (a.assignment_status === 'completed' || a.assignment_status === 'cancelled')
            return false;
          const assignmentDate = formatRouteDate(a.expected_visit_date);
          return assignmentDate === routeDate;
        });

        // If no assignments remain for this technician/date, delete the route
        if (technicianAssignments.length === 0) {
          await deleteRouteByTechnicianDate(technicianId, expectedVisitDate);
          // Invalidate route queries so other pages get fresh data
          queryClient.invalidateQueries({ queryKey: routeKeys.all });
          return;
        }

        // Get enrichment data from selected_recalls
        const vins = technicianAssignments.map(a => a.vin);
        interface SelectedRecallData {
          vin: string;
          campaign_number: string;
          recall_title: string | null;
          car_year: number | null;
          make_name: string | null;
          model_name: string | null;
          dealer_address: string | null;
          dealer_city: string | null;
          dealer_region: string | null;
          dealer_postal_code: string | null;
          dealer_phone: string | null;
          dealer_latitude: number | null;
          dealer_longitude: number | null;
          stock_number: string | null;
        }
        const recallDataMap = new Map<string, SelectedRecallData>();

        try {
          const prodClient = getProdSupabaseClient();
          const selectFields = [
            'vin',
            'campaign_number',
            'recall_title',
            'car_year',
            'make_name',
            'model_name',
            'dealer_address',
            'dealer_city',
            'dealer_region',
            'dealer_postal_code',
            'dealer_phone',
            'dealer_latitude',
            'dealer_longitude',
            'stock_number'
          ].join(', ');
          const { data: selectedRecalls } = await prodClient
            .from('selected_recalls')
            .select(selectFields)
            .in('vin', vins);

          if (selectedRecalls && Array.isArray(selectedRecalls)) {
            for (const recall of selectedRecalls as SelectedRecallData[]) {
              const key = `${recall.vin}|${recall.campaign_number}`;
              recallDataMap.set(key, recall);
            }
          }
        } catch {
          // Continue without enrichment data
        }

        // Transform assignments for route building
        const assignmentsForRoute = technicianAssignments.map(a => {
          const recallKey = `${a.vin}|${a.campaign_number}`;
          const recallData = recallDataMap.get(recallKey);

          const result: {
            id: string;
            vin: string;
            campaign_number: string;
            recall_title?: string;
            dealer_id?: string;
            dealer_name?: string;
            dealer_street?: string;
            dealer_city?: string;
            dealer_region?: string;
            dealer_postal_code?: string;
            dealer_phone?: string;
            dealer_latitude?: number;
            dealer_longitude?: number;
            vehicle_year?: number;
            vehicle_make?: string;
            vehicle_model?: string;
            estimated_duration_minutes?: number;
            assignment_status: string;
            stock_number?: string;
            // Manual stop fields
            stop_type?: 'recall' | 'manual';
            manual_stop_reason?: string;
            manual_stop_lat?: number;
            manual_stop_lng?: number;
            notes?: string;
            // Customer stops (set on the assignment by a DB trigger) — never names/phones
            stop_kind?: 'business' | 'customer';
            contact_id?: string | null;
            address_line2?: string | null;
            dispatcher_note?: string | null;
          } = {
            id: a.id,
            vin: a.vin,
            campaign_number: a.campaign_number,
            assignment_status: a.assignment_status
          };

          if (a.recall_title) result.recall_title = a.recall_title;
          if (a.dealer_id) result.dealer_id = a.dealer_id;
          if (a.dealer_name) result.dealer_name = a.dealer_name;
          if (a.estimated_duration_minutes)
            result.estimated_duration_minutes = a.estimated_duration_minutes;

          // Enrich with selected_recalls data
          if (recallData?.dealer_address) result.dealer_street = recallData.dealer_address;
          if (recallData?.dealer_city) result.dealer_city = recallData.dealer_city;
          if (recallData?.dealer_region) result.dealer_region = recallData.dealer_region;
          if (recallData?.dealer_postal_code)
            result.dealer_postal_code = recallData.dealer_postal_code;
          if (recallData?.dealer_phone) result.dealer_phone = recallData.dealer_phone;
          if (recallData?.dealer_latitude) result.dealer_latitude = recallData.dealer_latitude;
          if (recallData?.dealer_longitude) result.dealer_longitude = recallData.dealer_longitude;
          if (recallData?.car_year) result.vehicle_year = recallData.car_year;
          if (recallData?.make_name) result.vehicle_make = recallData.make_name;
          if (recallData?.model_name) result.vehicle_model = recallData.model_name;
          if (recallData?.stock_number) result.stock_number = recallData.stock_number;

          // Enrich recall_title from selected_recalls if not already set from assignment
          if (!result.recall_title && recallData?.recall_title) {
            result.recall_title = recallData.recall_title;
          }

          // Fallback to assignment's vehicle info (for manual stops without selected_recalls)
          if (!result.vehicle_year && a.vehicle_year) result.vehicle_year = a.vehicle_year;
          if (!result.vehicle_make && a.vehicle_make) result.vehicle_make = a.vehicle_make;
          if (!result.vehicle_model && a.vehicle_model) result.vehicle_model = a.vehicle_model;

          // Fallback to assignment's dealer info (for manual stops without selected_recalls)
          if (!result.dealer_street && a.dealer_address) result.dealer_street = a.dealer_address;
          if (!result.dealer_city && a.dealer_city) result.dealer_city = a.dealer_city;
          if (!result.dealer_region && a.dealer_region) result.dealer_region = a.dealer_region;
          if (!result.dealer_postal_code && a.dealer_postal_code)
            result.dealer_postal_code = a.dealer_postal_code;
          if (!result.dealer_phone && a.dealer_phone) result.dealer_phone = a.dealer_phone;

          // Include manual stop fields for route building
          if (a.stop_type) result.stop_type = a.stop_type;
          if (a.manual_stop_reason) result.manual_stop_reason = a.manual_stop_reason;
          if (a.manual_stop_lat) result.manual_stop_lat = a.manual_stop_lat;
          if (a.manual_stop_lng) result.manual_stop_lng = a.manual_stop_lng;
          if (a.notes) result.notes = a.notes;
          if (a.stop_kind) result.stop_kind = a.stop_kind;
          if (a.contact_id) result.contact_id = a.contact_id;
          if (a.address_line2) result.address_line2 = a.address_line2;
          if (a.dispatcher_note) result.dispatcher_note = a.dispatcher_note;

          return result;
        });

        // Get existing route to preserve start location for optimization
        let startLocation:
          | {
              type: 'tenant_default' | 'custom';
              address: string;
              lat: number;
              lng: number;
              display_name?: string;
            }
          | undefined;
        const { data: existingRoute } = await getRouteByTechnicianDate(technicianId, routeDate);
        if (existingRoute?.route_data?.start_location) {
          startLocation = existingRoute.route_data.start_location;
        }

        // Sync to route table with start location for optimization
        await syncAssignmentsToRoute(technicianId, routeDate, assignmentsForRoute, startLocation);

        // Invalidate route queries so other pages get fresh data
        queryClient.invalidateQueries({ queryKey: routeKeys.all });
      } catch {
        // Background operation - don't throw errors to user
      }
    },
    [queryClient]
  );

  // Refresh all assignment data. invalidateQueries triggers a background refetch
  // and resolves once it settles, so callers that `await refreshData()` still get
  // fresh data — but a failure here keeps the last good list rather than wiping it.
  const loadData = useCallback(async () => {
    setError(null);
    await queryClient.invalidateQueries({ queryKey: assignmentKeys.all });
  }, [queryClient]);

  // Create assignment
  const createAssignment = useCallback(
    async (data: CreateAssignmentData) => {
      try {
        setIsCreating(true);
        setError(null);

        // Check for existing assignment
        const existingAssignment = await checkExistingAssignment(data.vin, data.campaign_number);
        if (existingAssignment) {
          throw new Error('Assignment already exists for this VIN and campaign');
        }

        const result = await createTechnicianAssignment(data);

        logUserActivity({
          category: 'routing',
          action: 'create_assignment',
          entityType: 'assignment',
          entityId: data.vin,
          entityName: `${data.vin} - ${data.campaign_number}`,
          metadata: {
            vin: data.vin,
            campaign_number: data.campaign_number,
            technician_id: data.technician_id
          }
        });

        // Reconcile the cached list in the background (no awaited full reload)
        void loadData();

        // Sync the route table to include the new assignment.
        // Pass the just-created ID so the sync skips the race-prone re-query
        // wait and patches any visibility lag via direct PK fetch.
        if (data.technician_id && data.expected_visit_date && result?.id) {
          await syncToRouteTable(data.technician_id, data.expected_visit_date, [result.id]);
        }

        return result;
      } catch (err) {
        const metadata = createUserActionError(err, 'creating assignment');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      } finally {
        setIsCreating(false);
      }
    },
    [loadData, syncToRouteTable]
  );

  // Update assignment
  const updateAssignment = useCallback(
    async (id: string, data: UpdateAssignmentData) => {
      try {
        setIsUpdating(true);
        setError(null);

        // Get old assignment details for route sync (in case technician/date changed)
        const oldAssignment = assignments.find(a => a.id === id);
        const oldTechnicianId = oldAssignment?.technician_id;
        const oldExpectedVisitDate = oldAssignment?.expected_visit_date;

        const result = await updateTechnicianAssignment(id, data);

        logUserActivity({
          category: 'routing',
          action: 'update_assignment',
          entityType: 'assignment',
          entityId: id,
          metadata: { assignment_id: id, updates: data }
        });

        // Reconcile the cached list in the background (no awaited full reload)
        void loadData();

        // Sync the route table for the new technician/date.
        // Pass [id] so the sync skips the race-prone re-query wait and patches
        // any visibility lag via direct PK fetch.
        const newTechnicianId = data.technician_id || oldTechnicianId;
        const newExpectedVisitDate = data.expected_visit_date || oldExpectedVisitDate;
        if (newTechnicianId && newExpectedVisitDate) {
          await syncToRouteTable(newTechnicianId, newExpectedVisitDate, [id]);
        }

        // If technician or date changed, also sync the OLD route so it removes
        // the moved row. Don't pass IDs here — for the old (tech, date), the
        // re-query should now correctly EXCLUDE this row, and the 300ms wait
        // ensures UPDATE visibility for that exclusion. Passing [id] would
        // incorrectly re-add the moved row to the old route.
        const technicianChanged = data.technician_id && data.technician_id !== oldTechnicianId;
        const dateChanged =
          data.expected_visit_date && data.expected_visit_date !== oldExpectedVisitDate;
        if ((technicianChanged || dateChanged) && oldTechnicianId && oldExpectedVisitDate) {
          await syncToRouteTable(oldTechnicianId, oldExpectedVisitDate);
        }

        return result;
      } catch (err) {
        const metadata = createUserActionError(err, 'updating assignment');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [loadData, assignments, syncToRouteTable]
  );

  // Delete assignment (optimistic). The list and per-technician counts update
  // synchronously before any network call, so the routing board reflects the
  // removal instantly. Only the single-row DB delete is awaited; route-sync and
  // cache reconciliation run in the background. On failure the optimistic
  // removal is rolled back to the pre-delete snapshot.
  const deleteAssignment = useCallback(
    async (id: string) => {
      const snapshot =
        queryClient.getQueryData<TechnicianAssignment[]>(assignmentKeys.list()) ?? [];
      const assignmentToDelete = snapshot.find(a => a.id === id);
      const technicianId = assignmentToDelete?.technician_id;
      const expectedVisitDate = assignmentToDelete?.expected_visit_date;

      // 1. Instant UI: remove from the cached list immediately
      patchAssignments(prev => prev.filter(a => a.id !== id));

      logUserActivity({
        category: 'routing',
        action: 'delete_assignment',
        entityType: 'assignment',
        entityId: id,
        ...(assignmentToDelete?.vin && { entityName: assignmentToDelete.vin }),
        metadata: {
          assignment_id: id,
          vin: assignmentToDelete?.vin,
          campaign_number: assignmentToDelete?.campaign_number
        }
      });

      try {
        setIsDeleting(true);
        setError(null);

        // 2. Persist the deletion (single fast round-trip)
        await deleteTechnicianAssignment(id);

        // 3. Rebuild the route + reconcile the cache in the background — these
        //    must not block the caller or the UI (previously a 300ms sleep plus
        //    two full-table reads ran on the critical path).
        if (technicianId && expectedVisitDate) {
          void syncToRouteTable(technicianId, expectedVisitDate);
        }
        void queryClient.invalidateQueries({ queryKey: assignmentKeys.list() });
      } catch (err) {
        // 4. Roll back the optimistic removal
        queryClient.setQueryData(assignmentKeys.list(), snapshot);
        const metadata = createUserActionError(err, 'deleting assignment');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      } finally {
        setIsDeleting(false);
      }
    },
    [queryClient, patchAssignments, syncToRouteTable]
  );

  // Get assignments by technician
  const getAssignmentsByTechnician = useCallback(async (technicianId: string) => {
    try {
      const result = await getTechnicianAssignmentsByTechnician(technicianId);
      return result || [];
    } catch (err) {
      const metadata = createDataLoadingError(err, 'getting assignments by technician');
      setErrorMetadata(metadata);
      setError(metadata.userMessage);
      return [];
    }
  }, []);

  // Get available technicians for campaign
  const getAvailableTechnicians = useCallback(
    async (campaignNumber: string, skillLevel?: number) => {
      try {
        const result = await getAvailableTechniciansForCampaign(campaignNumber);
        if (result.error) {
          throw new Error(result.error.message);
        }
        return result.data || [];
      } catch (err) {
        const metadata = createDataLoadingError(err, 'getting available technicians');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        return [];
      }
    },
    []
  );

  // Computed statistics
  const statistics = useMemo(() => {
    const totalAssignments = assignments.length;
    const completedAssignments = assignments.filter(
      a => a.assignment_status === 'completed'
    ).length;
    const pendingAssignments = assignments.filter(a => a.assignment_status === 'assigned').length;
    const inProgressAssignments = assignments.filter(
      a => a.assignment_status === 'in_progress'
    ).length;
    const cancelledAssignments = assignments.filter(
      a => a.assignment_status === 'cancelled'
    ).length;

    const totalEstimatedHours = assignments.reduce((sum, assignment) => {
      return sum + (assignment.estimated_duration_minutes || 60) / 60;
    }, 0);

    const averageCompletionTime =
      completedAssignments > 0
        ? assignments
            .filter(a => a.assignment_status === 'completed' && a.started_at && a.completed_at)
            .reduce((sum, assignment) => {
              const start = new Date(assignment.started_at!);
              const end = new Date(assignment.completed_at!);
              return sum + (end.getTime() - start.getTime()) / (1000 * 60 * 60); // hours
            }, 0) / completedAssignments
        : 0;

    return {
      totalAssignments,
      completedAssignments,
      pendingAssignments,
      inProgressAssignments,
      cancelledAssignments,
      totalEstimatedHours,
      averageCompletionTime,
      completionRate: totalAssignments > 0 ? (completedAssignments / totalAssignments) * 100 : 0
    };
  }, [assignments]);

  // Group assignments by technician
  const assignmentsByTechnician = useMemo(() => {
    const grouped: Record<string, TechnicianAssignment[]> = {};
    assignments.forEach(assignment => {
      if (!grouped[assignment.technician_id]) {
        grouped[assignment.technician_id] = [];
      }
      grouped[assignment.technician_id]!.push(assignment);
    });
    return grouped;
  }, [assignments]);

  // Group assignments by status
  const assignmentsByStatus = useMemo(() => {
    const grouped: Record<string, TechnicianAssignment[]> = {};
    assignments.forEach(assignment => {
      if (!grouped[assignment.assignment_status]) {
        grouped[assignment.assignment_status] = [];
      }
      grouped[assignment.assignment_status]!.push(assignment);
    });
    return grouped;
  }, [assignments]);

  return {
    // Data
    assignments,
    workloadSummary,
    activeTechnicians,

    // Loading states. A failed load surfaces via the derived load-error message
    // while the last good data stays in place (action errors take precedence).
    loading,
    error: error ?? loadErrorMetadata?.userMessage ?? null, // backward compatible
    errorMetadata: errorMetadata ?? loadErrorMetadata, // modern error display
    isCreating,
    isUpdating,
    isDeleting,

    // Statistics
    statistics,
    assignmentsByTechnician,
    assignmentsByStatus,

    // Actions
    createAssignment,
    updateAssignment,
    deleteAssignment,
    syncToRouteTable,
    getAssignmentsByTechnician,
    getAvailableTechnicians,
    refreshData: loadData
  };
};

export default useTechnicianAssignments;
